// Backend integration point: Replace all mock data with real API calls
// Recommended APIs: Open-Meteo (free, no key needed), Windy Sounding API, 
// aviationweather.gov for METAR/TAF, rasp.net for soaring forecasts

export interface AerodromeInfo {
  icao: string;
  name: string;
  lat: number;
  lon: number;
  elevation: number;
  country: string;
}

export interface HourlyCondition {
  hour: string;
  time: number;
  temp: number;
  dewpoint: number;
  cloudCover: number;
  cloudCoverLow?: number;
  cloudCoverHigh?: number;
  cloudCoverMid?: number;
  shortwaveRadiation?: number;
  directRadiation?: number;
  diffuseRadiation?: number;
  sunshineDuration?: number;
  windSpeed: number;
  windDir: number;
  windSpeed120?: number;
  windDir120?: number;
  thermalStrength: number;
  cumulusBase: number;
  vz: number;
  visibility: number;
  isConvective: boolean;
  skyType?: 'cumulus' | 'blue' | 'stable'; // aerological sky classification
  gradient?: number; // thermal gradient °C/100m surface→850hPa
  precipProbability?: number; // precipitation probability %
  precipAmount?: number; // precipitation amount in mm for the hour
  snowfall?: number; // snowfall amount in cm for the hour when available
  weatherCode?: number; // WMO weather code
  liftedIndex?: number; // direct model LI when available, otherwise computed
  convectiveInhibition?: number | null; // CIN J/kg when available
  boundaryLayerHeight?: number | null; // BLH AGL-ish meters when available
  emagramStatus?: string; // hourly diagnosis from the vertical profile
  emagramSummary?: string; // plain-language emagram interpretation
  vzEmagram?: number; // VZ from emagram profile only
  vzEnergy?: number; // VZ from energy/solar/CAPE fallback
  vzCumulus?: number; // VZ floor from organized cumulus signal
  vzReason?: string; // source used for final VZ
  pilotSkySignal?: string; // field-style sky reading for pilots
  thermalTop?: number; // thermal top AGL in meters
  lcl?: number; // lifted condensation level AGL in meters
  inversionBase?: number | null; // inversion base AGL in meters
  windLayer?: number; // average wind in convective layer km/h
  windShear?: number; // wind shear in convective layer km/h
}

export interface DailySoaringForecast {
  date: string;
  dayLabel: string;
  verdict: 'favorable' | 'marginal' | 'unflyable' | 'storm-risk';
  qualityScore: number;
  sunrise: string;
  sunset: string;
  convectionStart: string;
  convectionEnd: string;
  maxCumulusBase: number;
  maxVZ: number;
  maxThermalStrength: number;
  stormRisk: number;
  waveConditions: boolean;
  convergence: boolean;
  overdevelopmentRisk: boolean;
  windShearFragmented: boolean;
  highCloudFilter: boolean; // true when high cloud cover reduces VZ/score
  highCloudCover: number; // % high cloud cover (cirrus layer)
  tTdSpread: number; // T - Td spread at midday (°C) — < 4°C = étalement risk
  precipProbabilityMax?: number; // max daily precipitation probability %
  precipTotalMm?: number; // daily precipitation total in mm
  rainRiskLabel?: string; // plain language rain/showers label
  rainWindowLabel?: string; // rain timing summary
  windSurface: { speed: number; dir: string };
  windGust: number; // surface wind gust in km/h
  windAloft2000: { speed: number; dir: string };
  windAloft5000: { speed: number; dir: string };
  triggerTemp: number;
  tempMin: number; // daily min temperature at 2m (°C)
  tempMax: number; // daily max temperature at 2m (°C)
  blDepth: number;
  cape: number;
  li: number;
  convectiveInhibition?: number | null;
  boundaryLayerHeight?: number | null;
  hourly: HourlyCondition[];
  // v1.2.0 — recalibrated fields
  dpvKm?: number;                // Distance Potentielle de Vol estimée (km)
  flightPotentialLabel?: string; // 'Favorable' | 'Marginal à favorable' | 'Marginal' | 'Faible' | 'Non propice' | 'Risque orageux'
  limitingFactors?: string[];    // List of active limiting factors
  confidence?: string;           // 'Élevée' | 'Bonne' | 'Modérée' | 'Faible'
  modelSourceNote?: string;      // Disclaimer note about data source
  modelSourceLabel?: string;     // Active weather model/source family
  cacheStatus?: 'fresh' | 'cached' | 'stale' | 'rate-limited';
  cacheMessage?: string;
  lastUpdated?: string;
  retryAfter?: string;
}

export const MOCK_AERODROMES: AerodromeInfo[] = [
  { icao: 'LFOI', name: 'Abbeville', lat: 50.143, lon: 1.832, elevation: 67, country: 'FR' },
  { icao: 'LFOP', name: 'Rouen Vallée de Seine', lat: 49.384, lon: 1.175, elevation: 151, country: 'FR' },
  { icao: 'LFPB', name: 'Paris Le Bourget', lat: 48.969, lon: 2.441, elevation: 66, country: 'FR' },
  { icao: 'LFMN', name: 'Nice Côte d\'Azur', lat: 43.658, lon: 7.215, elevation: 4, country: 'FR' },
  { icao: 'LFLL', name: 'Lyon Saint-Exupéry', lat: 45.726, lon: 5.090, elevation: 240, country: 'FR' },
  { icao: 'LFLB', name: 'Chambéry Savoie', lat: 45.638, lon: 5.880, elevation: 234, country: 'FR' },
  { icao: 'LFGJ', name: 'Dole-Tavaux', lat: 47.043, lon: 5.435, elevation: 189, country: 'FR' },
  { icao: 'LFCR', name: 'Rodez Marcillac', lat: 44.407, lon: 2.482, elevation: 647, country: 'FR' },
  { icao: 'EDDS', name: 'Stuttgart', lat: 48.690, lon: 9.222, elevation: 371, country: 'DE' },
  { icao: 'LSZH', name: 'Zurich', lat: 47.458, lon: 8.548, elevation: 432, country: 'CH' },
];

function generateHourly(sunrise: number, sunset: number, verdict: string): HourlyCondition[] {
  const hours: HourlyCondition[] = [];
  const convStart = sunrise + 2;
  const convEnd = sunset - 1;

  for (let h = sunrise; h <= sunset; h++) {
    const normalizedH = (h - sunrise) / (sunset - sunrise);
    const peakFactor = Math.sin(normalizedH * Math.PI);
    const isConv = h >= convStart && h <= convEnd;

    const baseTemp = 14 + peakFactor * 12;
    const tempVariance = (Math.sin(h * 1.7) * 0.8);
    
    const baseVZ = verdict === 'favorable' ? 2.8 : verdict === 'marginal' ? 1.4 : 0.4;
    const vzVariance = Math.sin(h * 2.3) * 0.3;

    hours.push({
      hour: `${h.toString().padStart(2, '0')}:00`,
      time: h,
      temp: Math.round((baseTemp + tempVariance) * 10) / 10,
      dewpoint: Math.round((baseTemp - 8 + Math.sin(h) * 1.5) * 10) / 10,
      cloudCover: isConv ? Math.round(30 + peakFactor * 40 + Math.sin(h * 1.3) * 15) : Math.round(10 + Math.sin(h) * 10),
      windSpeed: Math.round(12 + Math.sin(h * 0.8) * 6),
      windDir: Math.round(240 + Math.sin(h * 0.4) * 30),
      thermalStrength: isConv ? Math.round(peakFactor * 4 * 10) / 10 : 0,
      cumulusBase: isConv ? Math.round(800 + peakFactor * 1200 + vzVariance * 100) : 0,
      vz: isConv ? Math.round((baseVZ * peakFactor + vzVariance) * 10) / 10 : 0,
      visibility: Math.round(25 + Math.sin(h * 0.6) * 10),
      isConvective: isConv,
      precipProbability: 0,
      precipAmount: 0,
      weatherCode: 0,
    });
  }
  return hours;
}

export const MOCK_TODAY_FORECAST: DailySoaringForecast = {
  date: '2026-04-26',
  dayLabel: 'Aujourd\'hui',
  verdict: 'favorable',
  qualityScore: 78,
  sunrise: '06:24',
  sunset: '21:08',
  convectionStart: '10:30',
  convectionEnd: '18:45',
  maxCumulusBase: 2150,
  maxVZ: 3.2,
  maxThermalStrength: 4.1,
  stormRisk: 12,
  waveConditions: false,
  convergence: true,
  overdevelopmentRisk: false,
  windShearFragmented: false,
  highCloudFilter: false,
  highCloudCover: 15,
  tTdSpread: 8,
  windSurface: { speed: 14, dir: 'SO' },
  windGust: 18,
  windAloft2000: { speed: 22, dir: 'SSO' },
  windAloft5000: { speed: 35, dir: 'S' },
  triggerTemp: 19.5,
  tempMin: 12,
  tempMax: 24,
  blDepth: 2150,
  cape: 180,
  li: -2.1,
  hourly: generateHourly(6, 21, 'favorable'),
};

export const MOCK_WEEK_FORECAST: DailySoaringForecast[] = [
  MOCK_TODAY_FORECAST,
  {
    date: '2026-04-27',
    dayLabel: 'Lundi',
    verdict: 'marginal',
    qualityScore: 42,
    sunrise: '06:22',
    sunset: '21:10',
    convectionStart: '12:00',
    convectionEnd: '16:30',
    maxCumulusBase: 1200,
    maxVZ: 1.6,
    maxThermalStrength: 2.1,
    stormRisk: 25,
    waveConditions: false,
    convergence: false,
    overdevelopmentRisk: false,
    windShearFragmented: false,
    highCloudFilter: true,
    highCloudCover: 65,
    tTdSpread: 6,
    windSurface: { speed: 22, dir: 'O' },
    windGust: 30,
    windAloft2000: { speed: 38, dir: 'NO' },
    windAloft5000: { speed: 52, dir: 'N' },
    triggerTemp: 22.0,
    tempMin: 10,
    tempMax: 19,
    blDepth: 1200,
    cape: 320,
    li: -3.4,
    hourly: generateHourly(6, 21, 'marginal'),
  },
  {
    date: '2026-04-28',
    dayLabel: 'Mardi',
    verdict: 'unflyable',
    qualityScore: 8,
    sunrise: '06:20',
    sunset: '21:11',
    convectionStart: '--',
    convectionEnd: '--',
    maxCumulusBase: 0,
    maxVZ: 0,
    maxThermalStrength: 0,
    stormRisk: 5,
    waveConditions: false,
    convergence: false,
    overdevelopmentRisk: false,
    windShearFragmented: false,
    highCloudFilter: false,
    highCloudCover: 20,
    tTdSpread: 5,
    windSurface: { speed: 32, dir: 'NO' },
    windGust: 42,
    windAloft2000: { speed: 55, dir: 'N' },
    windAloft5000: { speed: 72, dir: 'NNO' },
    triggerTemp: 28.0,
    tempMin: 8,
    tempMax: 14,
    blDepth: 400,
    cape: 0,
    li: 2.1,
    hourly: generateHourly(6, 21, 'unflyable'),
  },
  {
    date: '2026-04-29',
    dayLabel: 'Mercredi',
    verdict: 'storm-risk',
    qualityScore: 35,
    sunrise: '06:18',
    sunset: '21:13',
    convectionStart: '11:00',
    convectionEnd: '14:00',
    maxCumulusBase: 1800,
    maxVZ: 2.4,
    maxThermalStrength: 3.2,
    stormRisk: 72,
    waveConditions: false,
    convergence: true,
    overdevelopmentRisk: true,
    windShearFragmented: false,
    highCloudFilter: false,
    highCloudCover: 10,
    tTdSpread: 7,
    windSurface: { speed: 18, dir: 'S' },
    windGust: 24,
    windAloft2000: { speed: 28, dir: 'SO' },
    windAloft5000: { speed: 42, dir: 'SO' },
    triggerTemp: 21.0,
    tempMin: 15,
    tempMax: 26,
    blDepth: 1800,
    cape: 850,
    li: -5.2,
    hourly: generateHourly(6, 21, 'marginal'),
  },
  {
    date: '2026-04-30',
    dayLabel: 'Jeudi',
    verdict: 'favorable',
    qualityScore: 85,
    sunrise: '06:16',
    sunset: '21:14',
    convectionStart: '10:00',
    convectionEnd: '19:00',
    maxCumulusBase: 2400,
    maxVZ: 3.8,
    maxThermalStrength: 4.8,
    stormRisk: 8,
    waveConditions: true,
    convergence: true,
    overdevelopmentRisk: false,
    windShearFragmented: false,
    highCloudFilter: false,
    highCloudCover: 5,
    tTdSpread: 10,
    windSurface: { speed: 10, dir: 'NE' },
    windGust: 14,
    windAloft2000: { speed: 18, dir: 'NE' },
    windAloft5000: { speed: 28, dir: 'ENE' },
    triggerTemp: 18.0,
    tempMin: 11,
    tempMax: 25,
    blDepth: 2400,
    cape: 95,
    li: -1.2,
    hourly: generateHourly(6, 21, 'favorable'),
  },
  {
    date: '2026-05-01',
    dayLabel: 'Vendredi',
    verdict: 'favorable',
    qualityScore: 91,
    sunrise: '06:14',
    sunset: '21:16',
    convectionStart: '09:45',
    convectionEnd: '19:30',
    maxCumulusBase: 2650,
    maxVZ: 4.1,
    maxThermalStrength: 5.2,
    stormRisk: 5,
    waveConditions: true,
    convergence: false,
    overdevelopmentRisk: false,
    windShearFragmented: false,
    highCloudFilter: false,
    highCloudCover: 8,
    tTdSpread: 12,
    windSurface: { speed: 8, dir: 'E' },
    windGust: 11,
    windAloft2000: { speed: 15, dir: 'ENE' },
    windAloft5000: { speed: 24, dir: 'NE' },
    triggerTemp: 17.5,
    tempMin: 13,
    tempMax: 27,
    blDepth: 2650,
    cape: 60,
    li: -0.8,
    hourly: generateHourly(6, 21, 'favorable'),
  },
  {
    date: '2026-05-02',
    dayLabel: 'Samedi',
    verdict: 'marginal',
    qualityScore: 55,
    sunrise: '06:12',
    sunset: '21:17',
    convectionStart: '11:30',
    convectionEnd: '17:00',
    maxCumulusBase: 1450,
    maxVZ: 2.0,
    maxThermalStrength: 2.6,
    stormRisk: 18,
    waveConditions: false,
    convergence: false,
    overdevelopmentRisk: false,
    windShearFragmented: false,
    highCloudFilter: true,
    highCloudCover: 40,
    tTdSpread: 5,
    windSurface: { speed: 20, dir: 'O' },
    windGust: 28,
    windAloft2000: { speed: 32, dir: 'NO' },
    windAloft5000: { speed: 48, dir: 'N' },
    triggerTemp: 23.5,
    tempMin: 9,
    tempMax: 18,
    blDepth: 1450,
    cape: 220,
    li: -2.8,
    hourly: generateHourly(6, 21, 'marginal'),
  },
];

export interface SoundingLevel {
  pressure: number;
  altitude: number;
  temp: number;
  dewpoint: number;
  windSpeed: number;
  windDir: number;
}

export function generateSounding(hour: number): SoundingLevel[] {
  const surfaceTemp = 10 + (hour - 6) * 1.2 - Math.pow((hour - 14), 2) * 0.15;
  const levels = [
    { pressure: 1013, altitude: 0 },
    { pressure: 950, altitude: 550 },
    { pressure: 925, altitude: 800 },
    { pressure: 900, altitude: 1050 },
    { pressure: 850, altitude: 1500 },
    { pressure: 800, altitude: 1960 },
    { pressure: 750, altitude: 2480 },
    { pressure: 700, altitude: 3050 },
    { pressure: 650, altitude: 3650 },
    { pressure: 600, altitude: 4300 },
    { pressure: 550, altitude: 5000 },
    { pressure: 500, altitude: 5740 },
    { pressure: 450, altitude: 6560 },
    { pressure: 400, altitude: 7450 },
    { pressure: 350, altitude: 8450 },
    { pressure: 300, altitude: 9600 },
  ];

  return levels.map((lvl, idx) => {
    const lapseRate = 6.5;
    const tempAtLevel = surfaceTemp - (lvl.altitude / 1000) * lapseRate;
    const inversionBonus = (idx === 3 || idx === 4) ? 2.5 : 0;
    const dewpointAtLevel = tempAtLevel - 8 - idx * 1.2 + Math.sin(idx) * 1.5;
    const windSpeedAtLevel = 12 + idx * 2.8 + Math.sin(idx * 0.7) * 4;
    const windDirAtLevel = 230 + idx * 5 + Math.sin(idx * 1.2) * 15;

    return {
      pressure: lvl.pressure,
      altitude: lvl.altitude,
      temp: Math.round((tempAtLevel + inversionBonus) * 10) / 10,
      dewpoint: Math.round(dewpointAtLevel * 10) / 10,
      windSpeed: Math.round(windSpeedAtLevel),
      windDir: Math.round(windDirAtLevel) % 360,
    };
  });
}
